/* global globalThis */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Database, Edit2, Copy, Download, Layers, Play, CheckCircle2, AlertTriangle, XCircle, Info, Gauge, ChevronDown, ChevronRight, PanelLeftClose, PanelLeftOpen, Loader2 } from 'lucide-react';
import Editor from '@monaco-editor/react';
import BlueGeckoIcon from '../../../../assets/chatbot.png';

const MainEditor = ({
  extractInstance,
  transformInstance,
  scLayer,
  scTableFields,
  scGeneratedSQL,
  scGeneratedScripts = [],
  scType = "SQL",
  scGenerating,
  handleRunQuery,
  scQueryRunning,
  scMappingDocs = [],
  scReviewSummary = null,
  scTransformationRules = [],
  activeScriptTab,
  setActiveScriptTab,
  scViewMode,
  setScViewMode,
  onFixWithAI
}) => {
  const [activeTab, setActiveTab] = useState('Results');
  const [isTablePanelOpen, setIsTablePanelOpen] = useState(true);
  const [copyStatus, setCopyStatus] = useState('Copy');
  const [fixingItems, setFixingItems] = useState({});
  const [bottomPanelHeight, setBottomPanelHeight] = useState(280);
  const [isResizingBottom, setIsResizingBottom] = useState(false);

  const startBottomResizing = useCallback((e) => {
    setIsResizingBottom(true);
    e.preventDefault();
  }, []);

  const stopBottomResizing = useCallback(() => {
    setIsResizingBottom(false);
  }, []);

  const doBottomResizing = useCallback((e) => {
    if (isResizingBottom) {
      const el = document.querySelector('.editor-results-container');
      if (el) {
        const bottom = el.getBoundingClientRect().bottom;
        const newHeight = bottom - e.clientY;
        if (newHeight > 100 && newHeight < 600) {
          setBottomPanelHeight(newHeight);
        }
      }
    }
  }, [isResizingBottom]);

  useEffect(() => {
    if (isResizingBottom) {
      globalThis.addEventListener('mousemove', doBottomResizing);
      globalThis.addEventListener('mouseup', stopBottomResizing);
    } else {
      globalThis.removeEventListener('mousemove', doBottomResizing);
      globalThis.removeEventListener('mouseup', stopBottomResizing);
    }
    return () => {
      globalThis.removeEventListener('mousemove', doBottomResizing);
      globalThis.removeEventListener('mouseup', stopBottomResizing);
    };
  }, [isResizingBottom, doBottomResizing, stopBottomResizing]);

  // Automatically select the first script tab when mode or scripts change
  useEffect(() => {
    const list = scViewMode === 'code' ? scGeneratedScripts : scTransformationRules;
    if (list && list.length > 0) {
      if (!activeScriptTab || !list.some(s => s.name === activeScriptTab)) {
        setActiveScriptTab(list[0].name);
      }
    } else {
      setActiveScriptTab(null);
    }
  }, [scGeneratedScripts, scTransformationRules, scViewMode, activeScriptTab, setActiveScriptTab]);

  const activeScriptContent = useMemo(() => {
    const list = scViewMode === 'code' ? scGeneratedScripts : scTransformationRules;
    if (!list || list.length === 0) return null;
    const found = list.find(s => s.name === activeScriptTab);
    return found ? found.content : list[0].content;
  }, [activeScriptTab, scGeneratedScripts, scTransformationRules, scViewMode]);

  const editorValue = activeScriptContent || scGeneratedSQL || "-- Write SQL here, or click 'Load Script' to load generated SQL\n-- Click a table in the schema sidebar to generate DDL";
  const editorLanguage = scType ? scType.toLowerCase() : "sql";

  const [expandedTables, setExpandedTables] = useState({});

  const toggleTable = (tableName) => {
    setExpandedTables(prev => ({ ...prev, [tableName]: !prev[tableName] }));
  };

  const handleCopy = () => {
    if (!editorValue) return;
    navigator.clipboard.writeText(editorValue);
    setCopyStatus('Copied!');
    setTimeout(() => setCopyStatus('Copy'), 2000);
  };

  const handleExport = () => {
    if (!editorValue) return;
    const blob = new Blob([editorValue], { type: 'text/sql' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeScriptTab || 'generated_script'}.sql`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const { title, tableList, tableFields } = useMemo(() => {
    let title = scLayer === 'Extract' ? 'SOURCE TABLES' : 'TARGET TABLES';
    let tableList = [];
    let tableFields = {};

    if (scTableFields?.results) {
      Object.keys(scTableFields.results).forEach(mappingId => {
        const mappingInfo = scTableFields.results[mappingId];
        if (mappingInfo?.status === 'success') {
          if (scLayer === 'Extract') {
            const sources = mappingInfo.discovery?.source_tables_for_db || [];
            sources.forEach(t => {
              if (!tableList.includes(t)) tableList.push(t);
            });
            const srcFields = mappingInfo.fields?.source || {};
            Object.keys(srcFields).forEach(t => {
              const cols = srcFields[t]?.columns || [];
              tableFields[t] = [...new Set([...(tableFields[t] || []), ...cols])];
            });
          } else {
            const targets = mappingInfo.discovery?.target_tables_normalized || [];
            targets.forEach(t => {
              if (!tableList.includes(t)) tableList.push(t);
            });
            const tgtFields = mappingInfo.fields?.target || {};
            Object.keys(tgtFields).forEach(t => {
              const cols = tgtFields[t] || [];
              tableFields[t] = [...new Set([...(tableFields[t] || []), ...cols])];
            });
          }
        }
      });
    }

    return { title, tableList, tableFields };
  }, [scLayer, scTableFields]);

  return (
    <div className="scriptly-editor-area">
      <div className="editor-status-bar">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
            <Database size={16} className="text-blue-500" />
          </div>
          {scTransformationRules && scTransformationRules.length > 0 ? (
            <div className="segmented-control p-1 bg-slate-200/50 rounded-lg flex gap-1">

              <button
                type="button"
                onClick={() => setScViewMode('code')}
                className={`segment-btn ${scViewMode === 'code' ? 'active shadow-sm' : ''}`}
              >
                Generated Code
              </button>
              <button
                type="button"
                onClick={() => setScViewMode('rules')}
                className={`segment-btn relative ${scViewMode === 'rules' ? 'active shadow-sm' : ''}`}
              >
                Transformation Rules
                {scTransformationRules.length > 0 && scViewMode !== 'rules' && (
                  <span className="absolute -top-1 -right-0.5 w-2 h-2 bg-blue-500 rounded-full border-2 border-slate-50 animate-pulse" />
                )}
              </button>
            </div>
          ) : (
            <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest px-4">
              Script Editor
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button className="status-bar-btn">
            <Edit2 size={12} />
            <span>Update Transformation Rule</span>
          </button>

          <button className="status-bar-btn" onClick={handleCopy}>
            <Copy size={12} />
            <span>{copyStatus}</span>
          </button>
          <button className="status-bar-btn" onClick={handleExport}>
            <Download size={12} />
            <span>Export</span>
          </button>
        </div>
      </div>

      <div className="sql-container">
        {/* Left Table List */}
        <div
          className="target-tables-panel transition-all duration-300 flex flex-col"
          style={{ marginTop: '12px', width: isTablePanelOpen ? 200 : 40, flexShrink: 0 }}
        >
          <button
            type="button"
            className={`panel-sub-header flex items-center ${isTablePanelOpen ? 'justify-between' : 'justify-center'} cursor-pointer hover:bg-slate-50 transition-colors`}
            onClick={() => setIsTablePanelOpen(!isTablePanelOpen)}
            title={isTablePanelOpen ? "Collapse Panel" : "Expand Panel"}
          >
            {isTablePanelOpen ? (
              <>
                <span className="truncate pr-2">{title}</span>
                <div className="flex items-center gap-2 shrink-0">
                  <PanelLeftClose size={14} className="text-slate-400 hover:text-blue-500 transition-colors" />
                </div>
              </>
            ) : (
              <PanelLeftOpen size={14} className="text-slate-400 hover:text-blue-500 transition-colors shrink-0" />
            )}
          </button>

          {isTablePanelOpen && (
            <div className="table-list mt-2 h-full overflow-y-auto custom-scrollbar border-t border-slate-200 pt-2">
              {tableList.map(table => (
                <div key={table} className="mb-1">
                  <button
                    type="button"
                    className="table-item flex items-center gap-2 cursor-pointer hover:bg-slate-50 py-1.5 px-4 w-full text-left"
                    onClick={() => toggleTable(table)}
                  >
                    <Layers size={12} className="text-slate-400 shrink-0" />
                    <span className="flex-1 text-[11px] text-slate-600 font-medium truncate">{table}</span>
                    <span className="text-slate-400 shrink-0 transition-transform duration-200">
                      {expandedTables[table] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </span>
                  </button>
                  {expandedTables[table] && tableFields[table] && (
                    <div className="pl-9 pr-2 py-1.5 flex flex-col gap-1.5 bg-slate-50">
                      {tableFields[table].map(col => (
                        <div key={col} className="text-[10px] text-slate-500 hover:text-blue-600 cursor-pointer truncate transition-colors">
                          {col}
                        </div>
                      ))}
                      {tableFields[table].length === 0 && (
                        <div className="text-[9px] text-slate-500 italic">No columns found</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {tableList.length === 0 && (
                <div className="text-[10px] text-slate-500 px-4 py-2 italic text-center">
                  No {title.toLowerCase()} available.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Center Editor */}
        <div className="flex-1 flex flex-col min-w-0" style={{ borderLeft: '1px solid #e2e8f0' }}>
          {/* Category Toggle & Tab Bar */}
          <div className="flex flex-col border-b border-slate-200">
            <div className="flex items-center px-4 h-10 justify-between">
              <div className="flex flex-1 overflow-x-auto custom-scrollbar items-center gap-1 pt-1">

                {(scViewMode === 'code' ? scGeneratedScripts : scTransformationRules).map((script) => (
                  <button
                    type="button"
                    key={script.name}
                    onClick={() => setActiveScriptTab(script.name)}
                    className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest cursor-pointer whitespace-nowrap border-b-2 transition-colors ${activeScriptTab === script.name
                      ? "border-blue-500 text-blue-600 bg-blue-50"
                      : "border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                      }`}
                  >
                    {script.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col flex-1 overflow-hidden relative min-w-0">
            <div className="flex-1 overflow-hidden min-h-0 shrink">
              <Editor
                height="100%"
                theme="vs-light"
                language={editorLanguage}
                value={editorValue}
                options={{
                  automaticLayout: true,
                  minimap: { enabled: false },
                  fontSize: 12,
                  scrollBeyondLastLine: false,
                  padding: { top: 10 },
                  fontFamily: "'Fira Code', 'Monaco', 'Courier New', monospace",
                  lineNumbers: 'on',
                  glyphMargin: true,
                  folding: true,
                  lineDecorationsWidth: 10,
                  readOnly: scGenerating,
                  wordWrap: 'on'
                }}
              />
            </div>

            <button
              type="button"
              className="results-resizer"
              aria-label="Resize results panel"
              onMouseDown={startBottomResizing}
            />

            {/* Bottom Tabs/Results */}
            <div
              className="border-t border-slate-200 bg-[#ffffff] flex flex-col editor-results-container"
              style={{ height: bottomPanelHeight }}
            >
              <div className="flex items-center px-4 border-b border-slate-100 h-10 gap-6">
                {['Compiled Code', 'Code Quality', 'Lineage', 'Commands'].map(tab => (
                  <button
                    type="button"
                    key={tab}
                    className={`editor-tab ${activeTab === tab ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
                <div className="ml-auto" style={{ marginLeft: 'auto' }}>
                  <button
                    onClick={handleRunQuery}
                    disabled={scQueryRunning}
                    className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 text-[10px] font-bold px-4 py-1.5 rounded flex items-center gap-1.5 transition-all shadow-lg active:scale-95"
                  >
                    <Play size={10} fill="currentColor" />
                    Validate
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto custom-scrollbar bg-slate-50/50">
                {activeTab === 'Results' && (
                  <div className="flex-1 flex flex-col items-center justify-center text-slate-500 gap-3 h-full">
                    <Database size={40} className="opacity-10" />
                    <span className="text-[11px] font-medium tracking-wide">Run a query to see results</span>
                  </div>
                )}

                {activeTab === 'Code Quality' && (
                  <div className="p-4 flex flex-col gap-4">
                    {scReviewSummary ? (
                      <>
                        {/* Summary Score Header */}
                        <div className="flex items-center gap-6 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                          <div className="relative w-16 h-16 flex items-center justify-center">
                            <Gauge size={48} className="text-blue-500 opacity-20" />
                            <span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-slate-800">
                              {scReviewSummary.average_score?.toFixed(0)}
                            </span>
                          </div>
                          <div className="flex-1">
                            <h4 className="text-[14px] font-bold text-slate-800 mb-1">Script Review Summary</h4>
                            <p className="text-[11px] text-slate-500">
                              Analysis completed for <span className="font-bold text-slate-700">{scReviewSummary.total} scripts</span>.
                              Average quality score: <span className="font-bold text-blue-600">{scReviewSummary.average_score}%</span>
                            </p>
                          </div>
                          <div className="flex gap-4">
                            <div className="text-center">
                              <div className="text-[14px] font-bold text-red-500">{scReviewSummary.bad?.count || 0}</div>
                              <div className="text-[9px] font-medium text-slate-400 uppercase">Bad</div>
                            </div>
                            <div className="text-center">
                              <div className="text-[14px] font-bold text-amber-500">{scReviewSummary.needs_improvement?.count || 0}</div>
                              <div className="text-[9px] font-medium text-slate-400 uppercase">Improve</div>
                            </div>
                            <div className="text-center">
                              <div className="text-[14px] font-bold text-emerald-500">{scReviewSummary.good?.count || 0}</div>
                              <div className="text-[9px] font-medium text-slate-400 uppercase">Good</div>
                            </div>
                          </div>
                        </div>

                        {/* Details Sections */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* BAD SECTION */}
                          {(scReviewSummary.bad?.tables || []).map((rev) => (
                            <div key={`bad-${rev.table_name}`} className="bg-white border-l-4 border-red-500 rounded-lg shadow-sm border border-slate-200 p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-[12px] font-bold text-slate-800 flex items-center gap-2">
                                  <XCircle size={14} className="text-red-500" />
                                  {rev.table_name}
                                </span>
                                <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-600 text-[9px] font-bold uppercase">{rev.script_type}</span>
                              </div>
                              <div className="flex flex-col gap-1.5">
                                {rev.required_changes?.map((change, cIdx) => {
                                  const fixKey = `bad-${rev.table_name}-${cIdx}`;
                                  const isFixing = fixingItems[fixKey];
                                  return (
                                    <div key={fixKey} className="fix-ai-row">
                                      <div className="flex items-start gap-2 text-[10px] text-slate-600 flex-1 min-w-0">
                                        <div className="w-1 h-1 rounded-full bg-red-400 mt-1.5 shrink-0" />
                                        <span className="flex-1">{change}</span>
                                      </div>
                                      {onFixWithAI && (
                                        <button
                                          type="button"
                                          className="fix-ai-btn fix-ai-btn-red"
                                          disabled={isFixing}
                                          onClick={async () => {
                                            setFixingItems(prev => ({ ...prev, [fixKey]: true }));
                                            try { await onFixWithAI(rev.table_name, change, rev.script_type); }
                                            finally { setFixingItems(prev => ({ ...prev, [fixKey]: false })); }
                                          }}
                                        >
                                          {isFixing ? (
                                            <Loader2 size={10} className="animate-spin" />
                                          ) : (
                                            <img src={BlueGeckoIcon} alt="AI" className="w-3 h-3 object-contain" />
                                          )}
                                          <span>{isFixing ? 'Fixing...' : 'Fix with AI'}</span>
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}

                          {/* NEEDS IMPROVEMENT SECTION */}
                          {(scReviewSummary.needs_improvement?.tables || []).map((rev) => (
                            <div key={`improve-${rev.table_name}`} className="bg-white border-l-4 border-amber-500 rounded-lg shadow-sm border border-slate-200 p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-[12px] font-bold text-slate-800 flex items-center gap-2">
                                  <AlertTriangle size={14} className="text-amber-500" />
                                  {rev.table_name}
                                </span>
                                <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 text-[9px] font-bold uppercase">{rev.script_type}</span>
                              </div>
                              <div className="flex flex-col gap-1.5">
                                {rev.required_changes?.map((change, cIdx) => {
                                  const fixKey = `improve-${rev.table_name}-${cIdx}`;
                                  const isFixing = fixingItems[fixKey];
                                  return (
                                    <div key={fixKey} className="fix-ai-row">
                                      <div className="flex items-start gap-2 text-[10px] text-slate-600 flex-1 min-w-0">
                                        <div className="w-1 h-1 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                                        <span className="flex-1">{change}</span>
                                      </div>
                                      {onFixWithAI && (
                                        <button
                                          type="button"
                                          className="fix-ai-btn fix-ai-btn-amber"
                                          disabled={isFixing}
                                          onClick={async () => {
                                            setFixingItems(prev => ({ ...prev, [fixKey]: true }));
                                            try { await onFixWithAI(rev.table_name, change, rev.script_type); }
                                            finally { setFixingItems(prev => ({ ...prev, [fixKey]: false })); }
                                          }}
                                        >
                                          {isFixing ? (
                                            <Loader2 size={10} className="animate-spin" />
                                          ) : (
                                            <img src={BlueGeckoIcon} alt="AI" className="w-3 h-3 object-contain" />
                                          )}
                                          <span>{isFixing ? 'Fixing...' : 'Fix with AI'}</span>
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}

                          {/* GOOD SECTION */}
                          {(scReviewSummary.good?.tables || []).map((name) => (
                            <div key={`good-${name}`} className="bg-white border-l-4 border-emerald-500 rounded-lg shadow-sm border border-slate-200 p-3">
                              <div className="flex items-center gap-2">
                                <CheckCircle2 size={14} className="text-emerald-500" />
                                <span className="text-[12px] font-bold text-slate-800">{name}</span>
                                <span className="ml-auto px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 text-[9px] font-bold uppercase tracking-wider">Passed</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="h-[200px] flex flex-col items-center justify-center text-slate-400 gap-2">
                        <CheckCircle2 size={40} className="opacity-10" />
                        <span className="text-[11px] font-medium tracking-wide">Generate a script to see code quality review</span>
                      </div>
                    )}
                  </div>
                )}

                {activeTab !== 'Results' && activeTab !== 'Code Quality' && (
                  <div className="flex-1 flex flex-col items-center justify-center text-slate-500 gap-3 h-full">
                    <Info size={40} className="opacity-10" />
                    <span className="text-[11px] font-medium tracking-wide">Content for {activeTab} is currently unavailable.</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

MainEditor.propTypes = {
  extractInstance: PropTypes.string,
  transformInstance: PropTypes.string,
  scLayer: PropTypes.string,
  scTableFields: PropTypes.object,
  scGeneratedSQL: PropTypes.string,
  scGeneratedScripts: PropTypes.array,
  scType: PropTypes.string,
  scGenerating: PropTypes.bool,
  handleRunQuery: PropTypes.func,
  scQueryRunning: PropTypes.bool,
  scMappingDocs: PropTypes.array,
  scReviewSummary: PropTypes.object,
  scTransformationRules: PropTypes.array,
  activeScriptTab: PropTypes.string,
  setActiveScriptTab: PropTypes.func,
  scViewMode: PropTypes.string,
  setScViewMode: PropTypes.func,
  onFixWithAI: PropTypes.func
};

export default MainEditor;